<template>
  <div>
    <PageHeader :title="title" :items="items"/>
    <b-row align-h="center">
      <b-col sm="12">
        <loader :loading="loading">
          <b-card>
            <b-row>

              <b-col sm="6">
                <b-card-body>
                  <b-row>
                    <div>
                      <h4>{{ $t('pages.campaign.edit.title') }}</h4>
                      <hr>
                    </div>

                    <b-col sm="6">
                      <div class="mb-3">
                        <label>{{ $t('pages.campaign.edit.campaign_name') }}</label>
                        <input
                            v-model="campaignName"
                            name="name"
                            type="text"
                            class="form-control"
                            :placeholder="$t('pages.campaign.edit.campaign_name_placeholder')"
                            :class="{
                        'is-invalid': $v.campaignName.$error,
                      }"
                        />
                        <div
                            v-if="!$v.campaignName.required"
                            class="invalid-feedback"
                        >
                          {{ $t('pages.campaign.edit.validation.campaign_name_required') }}
                        </div>
                        <div
                            v-if="!$v.campaignName.minLength"
                            class="invalid-feedback"
                        >
                          {{
                            $t('pages.campaign.edit.validation.campaign_name_min', {min: $v.campaignName.$params.minLength.min})
                          }}
                        </div>
                        <div
                            v-if="!$v.campaignName.maxLength"
                            class="invalid-feedback"
                        >
                          {{
                            $t('pages.campaign.edit.validation.campaign_name_max', {min: $v.campaignName.$params.maxLength.max})
                          }}
                        </div>
                      </div>
                    </b-col>

                    <b-col sm="6">
                      <div class="mb-3">
                        <label class="control-label">
                          {{ $t('pages.campaign.edit.campaign_status') }}
                          <hint>{{ $t('pages.campaign.edit.infos.status_info') }}</hint>
                        </label>
                        <b-form-select
                            class="form-select"
                            :options="statusOptions"
                            v-model="status"
                            text-field="name"
                            value-field="value">
                        </b-form-select>
                      </div>
                    </b-col>

                    <b-col sm="12">
                      <div class="mb-3">
                        <label>
                          {{ $t('pages.campaign.edit.marketplace') }}
                          <hint>{{ $t('pages.campaign.edit.infos.marketplace_info') }}</hint>
                        </label>
                        <market-place-component v-model="selectedMarketplaces"
                                                :selected-marketplace-ids="selectedMarketplaceIds"/>
                      </div>
                    </b-col>

                    <b-col xl="8" lg="12" md="12" sm="12">
                      <div class="mb-3">
                        <label class="control-label">
                          {{ $t('pages.campaign.edit.template') }}
                          <hint>{{ $t('pages.campaign.edit.infos.template_info') }}</hint>
                        </label>
                        <TemplateSelector :templates="templates"
                                          @selectedTemplate="selectedTemplate = $event"
                                          :defaultTemplate="selectedTemplate"/>
                      </div>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-col>

              <b-col sm="6">
                <b-card-body>
                  <b-row>
                    <div>
                      <h4>{{ $t('pages.campaign.edit.campaign_schedule') }}</h4>
                      <hr>
                    </div>
                    <b-col sm="12">
                      <campaign-alert :type="selectedTemplateType"/>
                    </b-col>
                    <b-col sm="12">
                      <div class="mb-3">
                        <label class="control-label">{{ $t('pages.campaign.edit.after_this') }}
                          <hint>{{ $t('pages.campaign.edit.infos.after_this_info') }}</hint>
                        </label>
                        <b-form-select
                            class="form-select"
                            :options="orderStatusOptions"
                            v-model="selectedOrderStatus"
                            text-field="name"
                            value-field="value">
                        </b-form-select>
                      </div>
                    </b-col>

                    <b-col sm="6">
                      <div class="mb-3">
                        <label class="control-label">{{ $t('pages.campaign.edit.after_day') }}
                          <hint>{{ $t('pages.campaign.edit.infos.after_day_info') }}</hint>
                        </label>
                        <div class="mb-2">
                          <quantity-input
                              v-model="selectedDay"
                              :min="min"
                              :max="30"
                          ></quantity-input>
                        </div>
                      </div>
                    </b-col>

                    <b-col sm="6">
                      <div class="mb-3">
                        <label class="control-label">{{ $t('pages.campaign.edit.time') }}
                          <hint>{{ $t('pages.campaign.edit.infos.time_info') }}</hint>
                        </label>
<!--                        <b-form-input-->
<!--                            id="time"-->
<!--                            v-model="selectedTime"-->
<!--                            type="time"-->
<!--                        />-->
                        <date-picker
                            :clearable="false"
                            v-model="selectedTime"
                            :minute-step="15"
                            format="HH:mm"
                            value-type="format"
                            type="time"
                            placeholder="HH:mm"
                        ></date-picker>
                      </div>
                    </b-col>

                    <b-col sm="12" class="mt-4">
                      <b-alert show variant="success">
                        <div v-html="$t('pages.campaign.edit.alert_message', {
                              day: selectedDay,
                              order_status: orderStatusOptions.find(x => x.value === selectedOrderStatus).name,
                              time: selectedTime
                            })">
                        </div>
                      </b-alert>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-col>

              <b-col>
                <b-card-body>
                  <b-row>
                    <div>
                      <h4>{{ $t('pages.campaign.edit.campaign_rules') }}</h4>
                      <hr>
                    </div>

                    <b-col sm="6">
                      <div class="mb-3">
                        <label class="control-label">{{ $t('pages.campaign.edit.buyer_purchase_count') }}
                          <hint>{{ $t('pages.campaign.edit.infos.buyer_purchase_count_info') }}</hint>
                        </label>
                        <div class="mb-3">
                          <quantity-input :min="0" :max="5" v-model="selectedPurchaseCount"></quantity-input>
                        </div>
                      </div>
                    </b-col>
                    <b-col sm="6">
                      <div class="mb-3">
                        <label class="control-label">{{ $t('pages.campaign.edit.asin_filter') }}
                          <hint>{{ $t('pages.campaign.edit.infos.asin_filter_info') }}</hint>
                        </label>
                        <b-tabs pills nav-class="bg-light rounded" content-class="mt-4" v-model="type">
                          <b-tab :title="$t('pages.campaign.edit.include')" active>
                            <b-card-text>
                              <div>
                                <product-tabs
                                    v-model="selectedIncludeFilters"
                                    regex="([A-Z0-9]{10})"
                                    :place-holder="$t('pages.campaign.edit.enter_asin')"
                                />
                                <b-btn
                                    size="sm"
                                    variant="link"
                                    @click="$bvModal.show('catalog-product-modal')"
                                >
                                  <i class="mdi mdi-magnify-plus-outline"></i>
                                  {{ $t('pages.campaign.edit.add_from_catalog') }}
                                </b-btn>
                              </div>
                            </b-card-text>
                          </b-tab>
                          <b-tab :title="$t('pages.campaign.edit.exclude')">
                            <b-card-text>
                              <div>
                                <product-tabs
                                    v-model="selectedExcludeFilters"
                                    regex="([A-Z0-9]{10})"
                                    :place-holder="$t('pages.campaign.edit.enter_asin')"
                                />
                                <b-btn
                                    size="sm"
                                    variant="link"
                                    @click="$bvModal.show('catalog-product-modal')"
                                >
                                  <i class="mdi mdi-magnify-plus-outline"></i>
                                  {{ $t('pages.campaign.edit.add_from_catalog') }}
                                </b-btn>
                              </div>
                            </b-card-text>
                          </b-tab>
                        </b-tabs>
                      </div>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-col>

            </b-row>

            <b-card-body class="text-end">
              <hr>
              <b-btn @click="$router.push({name: 'campaigns'})" variant="danger" class="mx-3">
                {{ $t('pages.campaign.edit.turn_back') }}
              </b-btn>
              <b-btn @click="update" variant="success">
                <i class="mdi mdi-content-save"></i>
                {{ $t('pages.campaign.edit.update') }}
              </b-btn>
            </b-card-body>

          </b-card>

        </loader>
      </b-col>
    </b-row>
    <exclude-filter @selected="setSelectedFilters"></exclude-filter>
    <!-- end row -->
  </div>
</template>
<script>
import {maxLength, minLength, required} from "vuelidate/lib/validators";
import MarketPlaceComponent from "../../../../components/widgets/MarketPlaceComponent";
import ExcludeFilter from "./ExcludeFilter";
import EmailService from "../../../../Services/Api/EmailService";
import CampaignService from "../../../../Services/Api/CampaignService";
import ProductTabs from "./ProductTabs";
import TemplateSelector from "../../../../components/widgets/TemplateSelector";
import QuantityInput from "../../../../components/widgets/QuantityInput";
import CampaignAlert from "./CampaignAlert";
import {campaignComputed} from "@/state/helpers";
import DatePicker from "vue2-datepicker";

export default {
  name: 'EditCampaign',
  components: {
    CampaignAlert,
    ProductTabs,
    MarketPlaceComponent,
    QuantityInput,
    TemplateSelector,
    ExcludeFilter,
    DatePicker
  },
  data() {
    return {
      title: this.$t('pages.campaign.edit.title'),
      items: [
        {
          text: this.$t('pages.campaign.edit.items.first'),
          href: "/campaigns",
        },
        {
          text: this.$t('pages.campaign.edit.items.second'),
          active: true,
        },
      ],
      statusOptions: [
        {
          name: this.$t('pages.campaign.edit.status_options.active'),
          value: true
        },
        {
          name: this.$t('pages.campaign.edit.status_options.disabled'),
          value: false
        }
      ],
      selectedOrderStatus: 'order_date',
      orderStatusOptions: [
        {
          name: this.$t('pages.campaign.edit.order_status.order_date'),
          value: "order_date"
        },
        {
          name: this.$t('pages.campaign.edit.order_status.delivery_date'),
          value: "delivery_date"
        },
        {
          name: this.$t('pages.campaign.edit.order_status.shipping_date'),
          value: "shipping_date"
        },
      ],
      campaignName: '',
      selectedPurchaseCount: 0,
      status: true,
      selectedDay: 2,
      selectedTime: "18:00",
      selectedIncludeFilters: null,
      selectedExcludeFilters: null,
      type: null,
      submitted: false,
      selectedTemplate: null,
      templates: [],
      selectedMarketplaces: [],
      selectedMarketplaceIds: [],
      loading: false,
      min: 1,
      currentValues: {}
    };
  },
  validations: {
    campaignName: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(355)
    },
  },
  computed: {
    ...campaignComputed
  },
  watch: {
    selectedTemplateType(newVal) {
      const type = this.templateTypeOptions[newVal]
      if (type) {
        this.selectedDay = type.validations?.minDay ?? this.selectedDay
        this.min = type.validations?.minDay ?? this.min
        let flag = false
        this.orderStatusOptions.forEach(item => {
          item.disabled = !type.validations[item.value];
          if (!item.disabled && !flag) {
            this.selectedOrderStatus = item.value
            flag = true
          }
        })
      } else {
        this.min = 1
        this.selectedDay = this.currentValues.selectedDay
        this.selectedOrderStatus = this.currentValues.selectedOrderStatus
        this.orderStatusOptions.map(item => item.disabled = false)
      }
    }
  },
  methods: {
    setSelectedFilters(e) {
      const type = this.type === 0 ? 'selectedIncludeFilters' : 'selectedExcludeFilters'
      this[type] = [...this[type], ...e.map(x => x.asin)]
    },
    async getCampaignTemplates() {
      const {data: templates} = await EmailService.campaignTemplates({})
      this.templates = templates.data
    },
    async getCampaign() {
      this.loading = true
      const id = this.$route.params.campaignId
      await CampaignService.show({id, query: ''}).then(res => {
        const campaign = res.data.data
        this.selectedOrderStatus = campaign.after
        this.selectedDay = campaign.after_day
        this.selectedTime = campaign.at_time
        this.status = campaign.status
        this.campaignName = campaign.name
        this.selectedPurchaseCount = campaign.buyer_count
        this.selectedTemplate = campaign.template
        this.selectedExcludeFilters = campaign.excludes
        this.selectedIncludeFilters = campaign.includes
        this.selectedMarketplaceIds = campaign.marketplaces

        this.currentValues.selectedDay = campaign.after_day
        this.currentValues.selectedOrderStatus = campaign.after

      })
      this.loading = false
    },
    async update() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      } else {
        const payload = {
          name: this.campaignName,
          marketplaces: this.selectedMarketplaces.map(x => x.marketplace_id),
          template_id: this.selectedTemplate.id,
          after: this.selectedOrderStatus,
          after_day: this.selectedDay,
          at_time: this.selectedTime,
          buyer_count: this.selectedPurchaseCount,
          status: this.status,
          excludes: this.selectedFilters
        }
        await CampaignService.update({id: this.$route.params.campaignId, payload})
      }
    },
  },
  created() {
    this.getCampaign().then(() => {
      this.getCampaignTemplates()
    })
  }
};
</script>
